import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { User, LogOut, Menu, ChevronDown } from 'lucide-react';
import NotificationBell from '../ui/NotificationBell';
import { performLogout } from '../../services/logoutService';
import authService from '../../api/authService';
import { apiGet } from '../../services/apiClient';

function resolveUserRoleLabel(userData) {
  if (!userData || typeof userData !== 'object') {
    return authService.getStoredUserRoleName() || '';
  }
  const name = userData.roleName || userData.role_name || '';
  if (typeof name === 'string' && name.trim()) {
    return name.trim();
  }
  return authService.getStoredUserRoleName() || '';
}

function resolveUserRoleSlug(userData) {
  if (!userData || typeof userData !== 'object') {
    return 'user';
  }
  return userData.role || userData.roleCode || userData.role_code || 'user';
}

const Header = ({ title, breadcrumbs = [], showNotifications = true }) => {
  const [user, setUser] = useState(null);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const profileDropdownRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userId = sessionStorage.getItem('userId');
        if (!userId) {
          // Fallback to sessionStorage if userId not found
          const userEmail = sessionStorage.getItem('userName') || sessionStorage.getItem('userEmail') || 'user@example.com';
          const userDetailsStr = sessionStorage.getItem('userDetails');
          let userRole = 'user';
          let userRoleLabel = authService.getStoredUserRoleName();
          if (userDetailsStr) {
            try {
              const userDetails = JSON.parse(userDetailsStr);
              userRole = resolveUserRoleSlug(userDetails);
              userRoleLabel = resolveUserRoleLabel(userDetails) || userRoleLabel;
            } catch (e) {
              // Ignore parse error
            }
          }
          const userName = userEmail.split('@')[0].replace(/[.-]/g, ' ');
          
          setUser({
            email: userEmail,
            role: userRole,
            roleLabel: userRoleLabel,
            name: userName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
          });
          return;
        }
        
        // Fetch user data from API
        const response = await apiGet(`/gateway/registration/api/signup/organization/users/${userId}`);
        
        if (response?.data) {
          const userData = response.data;
          const firstName = userData.firstName || '';
          const lastName = userData.lastName || '';
          // sessionStorage.setItem('userdata', userData);
          sessionStorage.setItem('userName', userData.fullName);
          const fullName = `${firstName} ${lastName}`.trim() || userData.email?.split('@')[0] || 'User';
          const userEmail = userData.email || sessionStorage.getItem('userEmail') || 'user@example.com';
          const userRole = resolveUserRoleSlug(userData);
          const userRoleLabel = resolveUserRoleLabel(userData);
          
          setUser({
            email: userEmail,
            role: userRole,
            roleLabel: userRoleLabel,
            name: fullName
          });
        } else {
          // Fallback to sessionStorage if API fails
          const userEmail = sessionStorage.getItem('userEmail') || sessionStorage.getItem('userName') || 'user@example.com';
          const userDetailsStr = sessionStorage.getItem('userDetails');
          let userRole = 'user';
          let userRoleLabel = authService.getStoredUserRoleName();
          if (userDetailsStr) {
            try {
              const userDetails = JSON.parse(userDetailsStr);
              userRole = resolveUserRoleSlug(userDetails);
              userRoleLabel = resolveUserRoleLabel(userDetails) || userRoleLabel;
            } catch (e) {
              // Ignore parse error
            }
          }
          const userName = userEmail.split('@')[0].replace(/[.-]/g, ' ');
          
          setUser({
            email: userEmail,
            role: userRole,
            roleLabel: userRoleLabel,
            name: userName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
          });
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Fallback to sessionStorage on error
        const userEmail = sessionStorage.getItem('userEmail') || sessionStorage.getItem('userName') || 'user@example.com';
        const userDetailsStr = sessionStorage.getItem('userDetails');
        let userRole = 'user';
        let userRoleLabel = authService.getStoredUserRoleName();
        if (userDetailsStr) {
          try {
            const userDetails = JSON.parse(userDetailsStr);
            userRole = resolveUserRoleSlug(userDetails);
            userRoleLabel = resolveUserRoleLabel(userDetails) || userRoleLabel;
          } catch (e) {
            // Ignore parse error
          }
        }
        const userName = userEmail.split('@')[0].replace(/[.-]/g, ' ');
        
        setUser({
          email: userEmail,
          role: userRole,
          roleLabel: userRoleLabel,
          name: userName.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        });
      }
    };
    
    fetchUserData();
  }, [location]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (profileDropdownRef.current && !profileDropdownRef.current.contains(event.target)) {
        setIsProfileDropdownOpen(false);
      }
    };

    if (isProfileDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileDropdownOpen]);

  const handleLogout = async () => {
    setIsProfileDropdownOpen(false);
    await performLogout({ navigate, reason: 'manual' });
  };

  const getRoleBadgeColor = (role) => {
    const roleColors = {
      'super-admin': { bg: '#0B2530', text: '#ffffff' },
      'admin': { bg: '#006600', text: '#ffffff' },
      'agent': { bg: '#006600', text: '#ffffff' },
      'user': { bg: '#0B2530', text: '#ffffff' },
      'developer': { bg: '#BB0000', text: '#ffffff' },
      'auditor': { bg: '#BB0000', text: '#ffffff' },
      'approver': { bg: '#006600', text: '#ffffff' },
      'billing-admin': { bg: '#0B2530', text: '#ffffff' }
    };
    return roleColors[role] || roleColors['user'];
  };

  const roleDisplayNames = {
    'super-admin': 'Super Admin',
    'admin': 'Admin',
    'agent': 'Agent',
    'user': 'End User',
    'developer': 'Developer',
    'auditor': 'Auditor',
    'approver': 'Approver',
    'billing-admin': 'Billing Admin'
  };

  if (!user) return null;

  const roleLabel = user.roleLabel || roleDisplayNames[user.role] || user.role;
  const badgeColor = getRoleBadgeColor(user.role);

  return (
    <header 
      className="sticky top-0 z-50 w-full border-b"
      style={{ 
        backgroundColor: '#ffffff',
        borderColor: '#E5EFF3',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
        margin: 0,
        padding: 0,
        position: 'sticky',
        top: 0
      }}
    >
      <div className="w-full px-4 sm:px-6 lg:px-8" style={{ margin: 0 }}>
        <div className="flex h-16 items-center justify-between" style={{ margin: 0, padding: 0 }}>
          {/* Left: Logo/Title and Breadcrumbs */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {title && (
              <h1 className="text-lg font-semibold hidden sm:block" style={{ color: '#0B2530' }}>
                {title}
              </h1>
            )}
            
            {breadcrumbs && breadcrumbs.length > 0 && (() => {
              // Filter out breadcrumbs that match the title to avoid duplication
              const filteredBreadcrumbs = breadcrumbs.filter(crumb => crumb.name !== title);
              
              return filteredBreadcrumbs.length > 0 ? (
                <nav className="hidden md:flex items-center gap-2" aria-label="Breadcrumb">
                  <ol className="flex items-center space-x-2">
                    {filteredBreadcrumbs.map((crumb, index) => (
                      <li key={index} className="flex items-center">
                        {index > 0 && (
                          <span className="mx-2" style={{ color: '#B0B0B0' }}>/</span>
                        )}
                        {index === filteredBreadcrumbs.length - 1 ? (
                          <span className="text-sm font-medium" style={{ color: '#0B2530' }}>
                            {crumb.name}
                          </span>
                        ) : (
                          <Link
                            to={crumb.href}
                            className="text-sm hover:underline transition-colors"
                            style={{ color: '#4A5B63' }}
                            onMouseEnter={(e) => e.currentTarget.style.color = '#006600'}
                            onMouseLeave={(e) => e.currentTarget.style.color = '#4A5B63'}
                          >
                            {crumb.name}
                          </Link>
                        )}
                      </li>
                    ))}
                  </ol>
                </nav>
              ) : null;
            })()}
          </div>

          {/* Right: Notifications and Profile */}
          <div className="flex items-center gap-3">
            {/* Notifications - Using NotificationBell component */}
            {showNotifications && (
              <NotificationBell />
            )}

            {/* Profile Dropdown */}
            <div className="relative" ref={profileDropdownRef}>
              <button
                onClick={() => setIsProfileDropdownOpen(!isProfileDropdownOpen)}
                className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all"
                style={{ 
                  backgroundColor: isProfileDropdownOpen ? '#F8FCFB' : 'transparent',
                  border: '1px solid',
                  borderColor: isProfileDropdownOpen ? '#E5EFF3' : 'transparent'
                }}
                onMouseEnter={(e) => {
                  if (!isProfileDropdownOpen) {
                    e.currentTarget.style.backgroundColor = '#F8FCFB';
                    e.currentTarget.style.borderColor = '#E5EFF3';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isProfileDropdownOpen) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                    e.currentTarget.style.borderColor = 'transparent';
                  }
                }}
              >
                {/* Avatar */}
                <div 
                  className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                  style={{ backgroundColor: badgeColor.bg }}
                >
                  {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                </div>
                
                {/* User Info */}
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium" style={{ color: '#0B2530' }}>
                    {user.name}
                  </div>
                  <div className="text-xs" style={{ color: '#8A9AA2' }}>
                    {roleLabel}
                  </div>
                </div>
                
                <ChevronDown 
                  className={`h-4 w-4 transition-transform ${isProfileDropdownOpen ? 'rotate-180' : ''}`}
                  style={{ color: '#8A9AA2' }}
                />
              </button>

              {/* Dropdown Menu */}
              {isProfileDropdownOpen && (
                <div 
                  className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg border py-2 z-50"
                  style={{ 
                    backgroundColor: '#ffffff',
                    borderColor: '#E5EFF3',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {/* Profile Header */}
                  <div className="px-4 py-3 border-b" style={{ borderColor: '#E5EFF3' }}>
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold text-white"
                        style={{ backgroundColor: badgeColor.bg }}
                      >
                        {user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate" style={{ color: '#0B2530' }}>
                          {user.name}
                        </div>
                        <div className="text-xs truncate" style={{ color: '#8A9AA2' }}>
                          {user.email}
                        </div>
                        <div className="mt-1">
                          <span 
                            className="inline-block px-2 py-0.5 text-xs font-medium rounded"
                            style={{ 
                              backgroundColor: badgeColor.bg + '20',
                              color: badgeColor.bg
                            }}
                          >
                            {roleLabel}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    <Link
                      to="/portal/profile?tab=personal"
                      className="flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                      style={{ color: '#0B2530' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#F8FCFB';
                        e.currentTarget.style.color = '#006600';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                        e.currentTarget.style.color = '#0B2530';
                      }}
                      onClick={() => setIsProfileDropdownOpen(false)}
                    >
                      <User size={16} />
                      My Profile
                    </Link>
                  </div>

                  {/* Logout */}
                  <div className="border-t py-1" style={{ borderColor: '#E5EFF3' }}>
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors"
                      style={{ color: '#BB0000' }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#FFE7E0';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                    >
                      <LogOut size={16} />
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 rounded-lg transition-colors"
              style={{ color: '#4A5B63' }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#F8FCFB';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
